import { useEffect, useState, useMemo } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Modal from '../../components/ui/Modal.jsx';
import Badge from '../../components/ui/Badge.jsx';
import ConfigWarning from '../../components/ui/ConfigWarning.jsx';

const APPROVER_TYPES = [
  { value: 'manager', label: 'Direct Manager' },
  { value: 'user', label: 'Specific Person' },
  { value: 'role', label: 'Role' },
];

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'approver', label: 'Approver' },
  { value: 'finance', label: 'Finance' },
];

function approverSummary(approvers) {
  if (!approvers || approvers.length === 0) return 'No steps';
  return approvers
    .map((a) => {
      if (a.type === 'manager') return 'Manager';
      if (a.label) return a.label;
      if (a.type === 'role') return a.value;
      return a.value?.substring(0, 8) + '...';
    })
    .join(' \u2192 ');
}

export default function ChainsPage() {
  const toast = useToast();
  const [chains, setChains] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingChain, setEditingChain] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [chainsRes, usersRes] = await Promise.all([
        adminApi.listChains(),
        adminApi.listUsers(),
      ]);
      setChains(chainsRes.chains || []);
      setUsers(usersRes.users || []);
    } catch (err) {
      toast.error(`Failed to load: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!search) return chains;
    const s = search.toLowerCase();
    return chains.filter(
      (c) =>
        c.name?.toLowerCase().includes(s) ||
        c.rowKey?.toLowerCase().includes(s) ||
        approverSummary(c.approvers).toLowerCase().includes(s)
    );
  }, [chains, search]);

  function countAssigned(chainId) {
    return users.filter((u) => u.chainId === chainId && u.isActive !== false).length;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  // Check for misconfigured chains
  const chainWarnings = [];
  for (const chain of chains) {
    const approvers = chain.approvers || [];
    for (const step of approvers) {
      if (step.type === 'user') {
        const u = users.find((x) => x.rowKey === step.value);
        if (!u) {
          chainWarnings.push(`Chain "${chain.name || chain.rowKey}": approver user not found (${step.value})`);
        } else if (u.role !== 'approver' && u.role !== 'admin' && u.role !== 'finance') {
          chainWarnings.push(`Chain "${chain.name || chain.rowKey}": ${u.displayName} has "${u.role}" role — needs "approver", "finance", or "admin" role to approve requests`);
        }
      }
    }
  }

  return (
    <div>
      {chainWarnings.map((w, i) => (
        <ConfigWarning key={i} message={w} link="/admin/users" linkText="Fix in People →" />
      ))}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight mb-2">Approval Chains</h1>
          <p className="text-sm text-muted-foreground">
            {chains.length} chain{chains.length !== 1 ? 's' : ''} &middot; Assign chains to people on
            the People page
          </p>
        </div>
        <Button onClick={() => setEditingChain({})}>Add approval chain</Button>
      </div>

      {chains.length > 3 && (
        <div className="mb-5">
          <Input
            placeholder="Search chains..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-[400px]"
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          {chains.length === 0
            ? 'No approval chains yet. Create one to define how leave requests get approved.'
            : 'No chains match your search.'}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {filtered.map((chain) => (
            <ChainCard
              key={chain.rowKey}
              chain={chain}
              assignedCount={countAssigned(chain.rowKey)}
              onClick={() => setEditingChain(chain)}
            />
          ))}
        </div>
      )}

      {editingChain !== null && (
        <ChainEditModal
          chain={editingChain}
          users={users}
          onClose={() => setEditingChain(null)}
          onSaved={async () => {
            setEditingChain(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

/* ---------- Visual Flow Card ---------- */

function ChainCard({ chain, assignedCount, onClick }) {
  const approvers = chain.approvers || [];

  return (
    <div
      onClick={onClick}
      className="rounded-lg border border-border bg-card p-5 cursor-pointer transition-colors hover:border-primary/50"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="font-semibold text-base">{chain.name || chain.rowKey}</span>
          <Badge variant={chain.isActive === false ? 'danger' : 'success'}>
            {chain.isActive === false ? 'Inactive' : 'Active'}
          </Badge>
        </div>
        <span className="text-[13px] text-muted-foreground">
          {assignedCount} {assignedCount === 1 ? 'person' : 'people'} assigned
        </span>
      </div>

      {/* Visual flow */}
      <div className="flex items-center flex-wrap">
        {/* Start node */}
        <FlowNode label="Employee submits" muted />
        <FlowArrow />

        {approvers.map((a, i) => (
          <span key={i} className="contents">
            <FlowNode
              label={a.label || (a.type === 'manager' ? 'Direct Manager' : a.type === 'role' ? `Role: ${a.value}` : a.value)}
              step={i + 1}
            />
            <FlowArrow />
          </span>
        ))}

        {/* End node */}
        <FlowNode label="Approved" success />
      </div>

      <div className="mt-2.5 text-xs text-muted-foreground">
        If any step rejects &rarr; request returns to employee with comments
      </div>
    </div>
  );
}

function FlowNode({ label, step, muted, success }) {
  let classes = 'inline-flex items-center gap-2 px-3.5 py-2 border rounded-md text-[13px] whitespace-nowrap';

  if (success) {
    classes += ' bg-emerald-50 text-emerald-600 border-emerald-200';
  } else if (muted) {
    classes += ' bg-background border-border text-muted-foreground';
  } else {
    classes += ' bg-background border-border text-foreground';
  }

  if (step) {
    classes += ' font-medium';
  }

  return (
    <div className={classes}>
      {step && (
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-[11px] font-bold shrink-0">
          {step}
        </span>
      )}
      {label}
    </div>
  );
}

function FlowArrow() {
  return (
    <span className="px-1.5 text-muted-foreground text-base">
      &rarr;
    </span>
  );
}

/* ---------- Edit Modal ---------- */

function ChainEditModal({ chain, users, onClose, onSaved }) {
  const toast = useToast();
  const isNew = !chain.rowKey;

  const [form, setForm] = useState({
    chainId: chain.rowKey || '',
    name: chain.name || '',
    approvers: chain.approvers && chain.approvers.length > 0
      ? chain.approvers.map((a) => ({ ...a }))
      : [{ type: 'manager', value: 'direct', label: '' }],
    isActive: chain.isActive !== false,
  });
  const [saving, setSaving] = useState(false);

  const activeUsers = useMemo(
    () => users.filter((u) => u.isActive !== false).sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '')),
    [users]
  );

  const assignedUsers = useMemo(
    () => users.filter((u) => u.chainId === chain.rowKey && u.isActive !== false),
    [users, chain.rowKey]
  );

  function updateApprover(index, field, value) {
    setForm((f) => {
      const approvers = f.approvers.map((a) => ({ ...a }));
      approvers[index] = { ...approvers[index], [field]: value };

      // Reset value/label when type changes
      if (field === 'type') {
        if (value === 'manager') {
          approvers[index].value = 'direct';
          approvers[index].label = '';
        } else {
          approvers[index].value = '';
          approvers[index].label = '';
        }
      }

      // Auto-set label when picking a user
      if (field === 'value' && approvers[index].type === 'user') {
        const u = activeUsers.find((u) => u.rowKey === value);
        approvers[index].label = u?.displayName || '';
      }
      if (field === 'value' && approvers[index].type === 'role') {
        approvers[index].label = value === 'admin' ? 'Admin' : value === 'approver' ? 'Approver' : value === 'finance' ? 'Finance' : value;
      }

      return { ...f, approvers };
    });
  }

  function addApprover() {
    setForm((f) => ({
      ...f,
      approvers: [...f.approvers, { type: 'manager', value: 'direct', label: '' }],
    }));
  }

  function removeApprover(index) {
    if (form.approvers.length <= 1) return;
    setForm((f) => ({
      ...f,
      approvers: f.approvers.filter((_, i) => i !== index),
    }));
  }

  async function handleSave() {
    if (!form.chainId.trim()) {
      toast.error('Chain ID is required');
      return;
    }
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    for (let i = 0; i < form.approvers.length; i++) {
      const a = form.approvers[i];
      if (!a.value) {
        toast.error(`Step ${i + 1}: please select a value`);
        return;
      }
    }

    setSaving(true);
    try {
      await adminApi.upsertChain({
        chainId: form.chainId.trim().toLowerCase().replace(/\s+/g, '-'),
        name: form.name.trim(),
        approvers: form.approvers,
        isActive: form.isActive,
      });
      toast.success(isNew ? `Created "${form.name}"` : `Updated "${form.name}"`);
      onSaved();
    } catch (err) {
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (assignedUsers.length > 0) {
      toast.error(`Cannot delete: ${assignedUsers.length} people are still assigned to this chain. Reassign them first.`);
      return;
    }
    if (!window.confirm(`Delete "${chain.name}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await adminApi.deleteChain(chain.rowKey);
      toast.success(`Deleted "${chain.name}"`);
      onSaved();
    } catch (err) {
      toast.error(`Delete failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isNew ? 'Add approval chain' : `Edit ${chain.name}`}
      width="640px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {!isNew && (
            <Button variant="danger" onClick={handleDelete} loading={saving}>
              Delete
            </Button>
          )}
          <Button onClick={handleSave} loading={saving}>
            {isNew ? 'Create chain' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Identity */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Chain ID (slug)"
            value={form.chainId}
            onChange={(e) => setForm((f) => ({ ...f, chainId: e.target.value.replace(/[^a-z0-9-_]/gi, '').toLowerCase() }))}
            placeholder="e.g. engineering-flow"
            disabled={!isNew}
          />
          <Input
            label="Display name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Engineering Flow"
          />
        </div>

        {/* Approval steps */}
        <div>
          <div className="font-semibold text-sm mb-3 pb-1 border-b border-border">
            Approval steps
          </div>
          <p className="text-xs text-muted-foreground mb-3 mt-0">
            When an employee submits a leave request, it flows through these steps in order.
            If any step rejects, the request returns to the employee with comments.
          </p>

          <div className="flex flex-col gap-2.5">
            {form.approvers.map((approver, i) => (
              <div key={i} className="rounded-md border border-border bg-card p-3.5">
                <div className="flex items-center gap-3 mb-2.5">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">
                    {i + 1}
                  </span>
                  <span className="font-medium text-[13px] flex-1">Step {i + 1}</span>
                  {form.approvers.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeApprover(i)}>
                      Remove
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Select
                    label="Approver type"
                    value={approver.type}
                    onChange={(e) => updateApprover(i, 'type', e.target.value)}
                    options={APPROVER_TYPES}
                  />

                  {approver.type === 'manager' && (
                    <div className="flex items-end">
                      <div className="w-full rounded-md border border-input bg-muted px-3.5 py-2.5 text-[13px] text-muted-foreground">
                        Auto-resolved from employee&apos;s manager
                      </div>
                    </div>
                  )}

                  {approver.type === 'user' && (
                    <Select
                      label="Person"
                      value={approver.value}
                      onChange={(e) => updateApprover(i, 'value', e.target.value)}
                      options={[
                        { value: '', label: '— Select person —' },
                        ...activeUsers.map((u) => ({
                          value: u.rowKey,
                          label: u.displayName || u.email,
                        })),
                      ]}
                    />
                  )}

                  {approver.type === 'role' && (
                    <Select
                      label="Role"
                      value={approver.value}
                      onChange={(e) => updateApprover(i, 'value', e.target.value)}
                      options={[
                        { value: '', label: '— Select role —' },
                        ...ROLE_OPTIONS,
                      ]}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          <Button
            variant="secondary"
            size="sm"
            onClick={addApprover}
            className="mt-2.5"
          >
            + Add step
          </Button>
        </div>

        {/* Active toggle */}
        <label className="flex items-center gap-2.5 p-3 bg-background border border-input rounded-md cursor-pointer">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          />
          <div>
            <div className="font-medium text-[13px]">Active</div>
            <div className="text-xs text-muted-foreground">
              Inactive chains cannot be assigned to new people.
            </div>
          </div>
        </label>

        {/* Assigned users (read-only, edit mode only) */}
        {!isNew && assignedUsers.length > 0 && (
          <div>
            <div className="font-semibold text-sm mb-2 pb-1 border-b border-border">
              Assigned people ({assignedUsers.length})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {assignedUsers.map((u) => (
                <Badge key={u.rowKey} variant="info">
                  {u.displayName || u.email}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
